import React, {
  type ComponentPropsWithoutRef,
  type MutableRefObject,
  type ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { createPortal } from 'react-dom'

import { usePrevious } from '../../hooks/usePrevious'

import { BOTTOM_SHEET_MOBILE_PORTAL_ID } from './BottomSheet.constants'
import { BottomSheetInner } from './BottomSheetInner'

export type VoidHandler = (data?: unknown) => void

const noop = () => {}

export const useBottomSheet = () => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<BottomSheetRefHandlers>({
    open: noop,
    close: noop,
  })

  const open = useCallback((data?: unknown) => {
    ref.current.open(data)
    setIsOpen(true)
  }, [])
  const close = useCallback((callBack?: VoidHandler) => {
    ref.current.close(callBack)
    setIsOpen(false)
  }, [])

  return {
    ref,
    open,
    close,
    isOpen,
  }
}

export interface BottomSheetRefHandlers {
  open: (data?: unknown) => void
  close: (callBack?: VoidHandler) => void
}

type BottomSheetInnerProps = ComponentPropsWithoutRef<typeof BottomSheetInner>
type InnerProps = Pick<
  BottomSheetInnerProps,
  'children' | 'dataName' | 'footer' | 'mobileMaxHeight' | 'noHeightShrink'
>

type Props = {
  ref: MutableRefObject<BottomSheetRefHandlers>
  title?: ReactNode
  subtitle?: ReactNode
  /** Вызывается перед анимацией закрытия */
  onClose?: VoidHandler
  /** Вызывается после анимации закрытия */
  onClosed?: VoidHandler
} & InnerProps

// eslint-disable-next-line react/display-name
export const BottomSheet = forwardRef<BottomSheetRefHandlers, Props>(
  (props, ref) => {
    const { onClose, onClosed } = props

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)

    const [open, setOpen] = useState<boolean | null>(null)
    const [mounted, setMounted] = useState<boolean>(false)
    const callBackRef = useRef<VoidHandler | undefined>()
    const [data, setData] = useState<unknown>()

    useImperativeHandle(ref, () => ({
      open: (data: unknown) => {
        setMounted(true)
        setOpen(true)
        setData(data)
      },
      close: (callBack?: VoidHandler) => {
        setOpen(false)
        callBackRef.current = callBack
      },
    }))

    const prevOpen = usePrevious(open)
    useEffect(() => {
      if (prevOpen && !open) {
        onClose?.()
      }
    }, [onClose, open, prevOpen])

    const prevMounted = usePrevious(mounted)
    useEffect(() => {
      if (prevMounted && !mounted) {
        onClosed?.()
        if (typeof callBackRef.current === 'function') {
          callBackRef.current()
          callBackRef.current = undefined
        }
      }
    }, [prevMounted, mounted, onClosed])

    const onChildrenUnmounted = useCallback(() => {
      setMounted(false)
    }, [])

    if (!mounted) {
      return null
    }

    const element = (
      <BottomSheetInner
        {...props}
        isIOS={isIOS}
        data={data}
        open={open}
        setOpen={setOpen}
        onUnmounted={onChildrenUnmounted}
      />
    )

    const mobilePortal = document.getElementById(BOTTOM_SHEET_MOBILE_PORTAL_ID)

    if (!mobilePortal) {
      throw new Error('Портал для BottomSheet не найден.')
    }

    return createPortal(element, mobilePortal)
  },
)
